/**
 * Sub-bar state for the Backtest chart's forming candle.
 *
 * During tick replay the chart paints the *currently forming* bar at ~30fps. The
 * data behind that is either raw ticks (M1-M6, up to ~750 rows per bar) or M1
 * bars (M15+), but the animation only ever shows S discrete steps — so keeping
 * the rows around would be pure waste. Instead every bar is folded **once, at
 * ingest**, into S cumulative snapshots, after which:
 *
 *   - painting frame k is an O(1) typed-array read, zero allocation
 *   - memory is independent of tick density (~14 KiB total, see below)
 *
 * The fold reproduces the closed bar exactly at the last step. OHLC aggregation
 * is `first/max/min/last` and volume is a plain sum, all associative, so folding
 * ticks — or folding the M1 bars those ticks produced — lands on the same values
 * the datafeed's own aggregation produces. That is what stops the candle jumping
 * when the real closed bar replaces it. Callers must feed *mid* price and
 * *bid* volume, because that is what the datafeed builds its M1 bars from
 * (bi5_parser.py: `mid = (ask+bid)/2`, `bid_volume.sum()`).
 */

/** One row of sub-bar input: a raw tick (o=h=l=c=mid) or an M1 bar. */
export interface SubBarRow {
  /**
   * Row span start, unix MILLISECONDS — ticks are ms-resolution, so this is the
   * one place the codebase carries ms rather than seconds. Bar-sourced rows pass
   * `barTimeSec * 1000`.
   */
  timeMs: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

/** A forming-bar snapshot. Structurally a `CandleData` with volume required. */
export interface FormingBarState {
  /** Bar start, unix seconds — the *closed* bar's timestamp. */
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

/** Snapshots per bar. 32 covers the 30 steps 0.5x needs at the 33ms frame floor. */
export const DEFAULT_STEPS = 32
/** Bars held at once. 16 is ~4s of runway at 2x — enough to absorb a round trip. */
export const DEFAULT_CAPACITY = 16
/** Ceiling on remembered "no data here" bars, so the set can't grow unbounded. */
const MAX_UNAVAILABLE = 512

export class FormingBarBuffer {
  private readonly capacity: number
  private readonly steps: number

  // Flat typed arrays, preallocated. Prices are Float64: Float32's 24-bit
  // mantissa is ~59% of a point on a BTCUSD-scale price, and the whole delta is
  // 7KB. Volume is Float32 — it is display-only and never compared for equality.
  private readonly barTimes: Float64Array
  private readonly opens: Float64Array
  private readonly highs: Float64Array
  private readonly lows: Float64Array
  private readonly closes: Float64Array
  private readonly volumes: Float32Array

  /** barTime (unix s) -> ring slot. */
  private readonly slots = new Map<number, number>()
  /** Bars known to have no sub-bar data; never re-requested. */
  private readonly unavailable = new Set<number>()
  /** Next slot to write; the ring overwrites the oldest bar. */
  private writeSlot = 0

  constructor(capacity: number = DEFAULT_CAPACITY, steps: number = DEFAULT_STEPS) {
    this.capacity = Math.max(1, capacity)
    this.steps = Math.max(1, steps)
    const cells = this.capacity * this.steps
    this.barTimes = new Float64Array(this.capacity)
    this.opens = new Float64Array(this.capacity)
    this.highs = new Float64Array(cells)
    this.lows = new Float64Array(cells)
    this.closes = new Float64Array(cells)
    this.volumes = new Float32Array(cells)
  }

  /** Snapshots per bar — the caller's step index must be `< stepCount()`. */
  stepCount(): number {
    return this.steps
  }

  has(barTime: number): boolean {
    return this.slots.has(barTime)
  }

  isUnavailable(barTime: number): boolean {
    return this.unavailable.has(barTime)
  }

  /**
   * Remember that a bar has no sub-bar data (empty reply, or a failed fetch), so
   * the prefetcher stops asking. Bounded; oldest entries are forgotten first.
   */
  markUnavailable(barTime: number): void {
    if (this.unavailable.has(barTime)) return
    this.unavailable.add(barTime)
    if (this.unavailable.size > MAX_UNAVAILABLE) {
      const oldest = this.unavailable.values().next()
      if (!oldest.done) this.unavailable.delete(oldest.value)
    }
  }

  /**
   * Fold one bar's rows into S cumulative snapshots.
   *
   * `rows` must be ascending by `timeMs` — `close` takes the last row in each
   * step, so out-of-order input would report the wrong close. Both supply paths
   * are server-sorted. Rows outside the bar are clamped into the edge steps
   * rather than dropped, so a boundary row can't silently vanish.
   *
   * Returns false (and marks the bar unavailable) when there is nothing to fold.
   */
  ingest(barTime: number, barDurationSec: number, rows: readonly SubBarRow[]): boolean {
    if (rows.length === 0 || barDurationSec <= 0) {
      this.markUnavailable(barTime)
      return false
    }

    const slot = this.slots.get(barTime) ?? this.claimSlot(barTime)
    const base = slot * this.steps
    const stepMs = (barDurationSec * 1000) / this.steps
    const barStartMs = barTime * 1000
    const last = this.steps - 1

    // Pass 1: per-step aggregates. `closes` doubles as the has-data marker via
    // NaN, so no separate flag array is needed.
    for (let k = 0; k < this.steps; k++) {
      this.highs[base + k] = Number.NEGATIVE_INFINITY
      this.lows[base + k] = Number.POSITIVE_INFINITY
      this.closes[base + k] = Number.NaN
      this.volumes[base + k] = 0
    }

    for (const row of rows) {
      const raw = Math.floor((row.timeMs - barStartMs) / stepMs)
      const k = raw < 0 ? 0 : raw > last ? last : raw
      const i = base + k
      if (row.high > this.highs[i]) this.highs[i] = row.high
      if (row.low < this.lows[i]) this.lows[i] = row.low
      this.closes[i] = row.close
      this.volumes[i] += row.volume
    }

    // Pass 2: make it cumulative. Seeded from the bar's open so a step before the
    // first row reads as a doji at the open rather than as missing — we genuinely
    // know nothing else about it yet.
    const open = rows[0].open
    this.opens[slot] = open
    let runHigh = open
    let runLow = open
    let runClose = open
    let runVolume = 0

    for (let k = 0; k < this.steps; k++) {
      const i = base + k
      if (!Number.isNaN(this.closes[i])) {
        if (this.highs[i] > runHigh) runHigh = this.highs[i]
        if (this.lows[i] < runLow) runLow = this.lows[i]
        runClose = this.closes[i]
      }
      runVolume += this.volumes[i]
      this.highs[i] = runHigh
      this.lows[i] = runLow
      this.closes[i] = runClose
      this.volumes[i] = runVolume
    }

    this.unavailable.delete(barTime)
    return true
  }

  /**
   * Read the snapshot at `step` into `out`, returning false when the bar isn't
   * buffered. Writes into a caller-owned object so the animation loop allocates
   * nothing per frame.
   */
  stateAt(barTime: number, step: number, out: FormingBarState): boolean {
    const slot = this.slots.get(barTime)
    if (slot === undefined) return false

    const k = step < 0 ? 0 : step > this.steps - 1 ? this.steps - 1 : step
    const i = slot * this.steps + k

    out.time = barTime
    out.open = this.opens[slot]
    out.high = this.highs[i]
    out.low = this.lows[i]
    out.close = this.closes[i]
    out.volume = this.volumes[i]
    return true
  }

  /**
   * Drop all folded bars. Called when the bar grid itself changes — timeframe or
   * symbol change, a window reload, or the cursor moving backwards — since
   * buffered bars would then belong to a different grid. The unavailable set is
   * kept: "this symbol has no ticks" survives a timeframe change.
   */
  clear(): void {
    this.slots.clear()
    this.writeSlot = 0
    this.barTimes.fill(0)
  }

  /** Forget unavailability too — for a symbol change, where it no longer applies. */
  reset(): void {
    this.clear()
    this.unavailable.clear()
  }

  private claimSlot(barTime: number): number {
    const slot = this.writeSlot
    this.writeSlot = (this.writeSlot + 1) % this.capacity

    // Evict whoever held this slot before.
    const evicted = this.barTimes[slot]
    if (evicted !== 0) this.slots.delete(evicted)

    this.barTimes[slot] = barTime
    this.slots.set(barTime, slot)
    return slot
  }
}
